import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heart, MapPin, Truck, Search } from "lucide-react";
import FavoriteButton from "@/components/FavoriteButton";

export default function FavoritesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/favorites")
      .then(r => setItems(r.data?.items || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-start gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-rose-100 grid place-items-center">
            <Heart className="w-6 h-6 text-rose-600 fill-rose-600" />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-blue-950">Mis favoritos</h1>
            <p className="text-slate-600 mt-1 text-base">Las publicaciones que has guardado para revisar después.</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-12">Cargando…</div>
        ) : items.length === 0 ? (
          <Card className="p-10 rounded-2xl border border-dashed border-slate-300 text-center" data-testid="favorites-empty">
            <Heart className="w-10 h-10 text-rose-300 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">Aún no tienes favoritos</p>
            <p className="text-sm text-slate-500 mt-1">Toca el corazón en cualquier publicación para guardarla aquí.</p>
            <Link to="/medicamentos" className="inline-block mt-4">
              <Button className="bg-blue-600 hover:bg-blue-700 h-12 rounded-xl" data-testid="favorites-browse-btn">
                <Search className="w-4 h-4 mr-2" /> Ver medicamentos disponibles
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((it) => (
              <Link key={it.id} to={`/medicamento/${it.id}`} data-testid={`fav-card-${it.id}`}>
                <Card className="rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg transition-shadow relative">
                  <div className="absolute top-3 right-3 z-10">
                    <FavoriteButton listingId={it.id} />
                  </div>
                  <div className="aspect-[4/3] bg-slate-100">
                    <img src={it.photo_front} alt={it.medicine_name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-display font-semibold text-base text-slate-900 line-clamp-2">{it.medicine_name}</h3>
                      <Badge className="bg-blue-100 text-blue-900 hover:bg-blue-100 whitespace-nowrap">{it.category}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">{it.total_units} {it.unit_label}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        <div className="text-xs text-slate-500">Costo</div>
                        <div className="text-xl font-bold text-blue-900">${it.base_cost.toFixed(0)} <span className="text-sm font-normal text-slate-500">MXN</span></div>
                      </div>
                      <div className="text-right text-xs text-slate-500">
                        <div className="flex items-center gap-1 justify-end"><MapPin className="w-3.5 h-3.5"/>{it.zone}</div>
                        {it.delivery_cost > 0 && <div className="flex items-center gap-1 justify-end mt-1"><Truck className="w-3.5 h-3.5"/>+${it.delivery_cost.toFixed(0)} envío</div>}
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
